import { useApi } from '@/hooks/useApi';
import {
  Clock,
  AlertTriangle,
  Users,
  CheckCircle2,
  Loader2,
  ShieldAlert,
} from 'lucide-react';

interface HosDriver {
  driver_id: string;
  driver_name: string;
  vehicle_name: string;
  current_duty_status: string;
  total_driving_hours: number;
  total_on_duty_hours: number;
  violation_count: number;
  status: 'compliant' | 'warning' | 'violation';
  date: string;
}

interface HosViolation {
  log_id: string;
  driver_id: string;
  driver_name: string;
  date: string;
  rule: string;
  description: string;
  severity: 'warning' | 'violation';
  detected_at: string;
}

interface HosData {
  drivers: HosDriver[];
  violations: HosViolation[];
  metrics: {
    active_drivers: number;
    violations_today: number;
    avg_driving_hours: number;
    compliant_pct: number;
  };
}

const MOCK_DATA: HosData = {
  metrics: {
    active_drivers: 12,
    violations_today: 2,
    avg_driving_hours: 6.4,
    compliant_pct: 83,
  },
  drivers: [
    {
      driver_id: '1',
      driver_name: 'James Wright',
      vehicle_name: 'Truck-001',
      current_duty_status: 'driving',
      total_driving_hours: 9.5,
      total_on_duty_hours: 11.2,
      violation_count: 1,
      status: 'warning',
      date: new Date().toISOString().slice(0, 10),
    },
    {
      driver_id: '2',
      driver_name: 'Maria Santos',
      vehicle_name: 'Van-004',
      current_duty_status: 'on_duty',
      total_driving_hours: 5.1,
      total_on_duty_hours: 7.3,
      violation_count: 0,
      status: 'compliant',
      date: new Date().toISOString().slice(0, 10),
    },
  ],
  violations: [
    {
      log_id: 'v1',
      driver_id: '1',
      driver_name: 'James Wright',
      date: new Date().toISOString().slice(0, 10),
      rule: '30_minute_break',
      description: 'Driver has been driving more than 8 hours without a 30-minute break',
      severity: 'warning',
      detected_at: new Date().toISOString(),
    },
  ],
};

const dutyColors: Record<string, string> = {
  driving: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  on_duty: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  off_duty: 'bg-muted-foreground/20 text-muted-foreground border-border',
  sleeper_berth: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const dutyLabels: Record<string, string> = {
  driving: 'Driving',
  on_duty: 'On Duty',
  off_duty: 'Off Duty',
  sleeper_berth: 'Sleeper',
};

const statusColors: Record<string, string> = {
  compliant: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  violation: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const severityColors: Record<string, string> = {
  warning: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  violation: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const ruleLabels: Record<string, string> = {
  '11_hour_driving': '11-Hour Driving',
  '14_hour_window': '14-Hour Window',
  '30_minute_break': '30-Minute Break',
  '60_70_hour_limit': '60/70-Hour Limit',
};

function HoursBar({ value, max, warn }: { value: number; max: number; warn?: boolean }) {
  const pct = Math.min(100, (value / max) * 100);
  const overLimit = value > max * 0.8;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${overLimit || warn ? 'bg-amber-500' : 'bg-emerald-500'} ${value > max ? 'bg-red-500' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-mono w-12 text-right ${value > max ? 'text-red-400' : 'text-muted-foreground'}`}>
        {value.toFixed(1)}/{max}h
      </span>
    </div>
  );
}

export default function HosPage() {
  const { data, isLoading } = useApi<HosData>('/api/hos');
  const { data: violationsData } = useApi<HosViolation[]>('/api/hos/violations');

  const d = data ?? MOCK_DATA;
  const drivers = d.drivers ?? [];
  const violations = violationsData ?? d.violations ?? [];
  const metrics = d.metrics ?? MOCK_DATA.metrics;

  const metricCards = [
    { label: 'Active Drivers', value: metrics.active_drivers, icon: Users, color: 'text-blue-400' },
    { label: 'Violations Today', value: metrics.violations_today, icon: AlertTriangle, color: 'text-red-400' },
    { label: 'Avg Driving Hours', value: metrics.avg_driving_hours.toFixed(1) + 'h', icon: Clock, color: 'text-emerald-400' },
    { label: 'Compliant %', value: metrics.compliant_pct + '%', icon: CheckCircle2, color: 'text-emerald-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <Clock className="w-6 h-6 text-emerald-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">ELD / Hours of Service</h1>
          <p className="text-sm text-muted-foreground">Real-time driver compliance monitoring</p>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((m) => (
          <div key={m.label} className="bg-card/50 backdrop-blur border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{m.label}</span>
              <m.icon className={`w-4 h-4 ${m.color}`} />
            </div>
            <span className="text-2xl font-bold text-foreground">{m.value}</span>
          </div>
        ))}
      </div>

      {/* Driver HOS Table */}
      <div className="bg-card/50 backdrop-blur border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Driver Hours — Today</h2>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No active drivers today</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Driver</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 min-w-[160px]">Driving (11h limit)</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3 min-w-[160px]">On-Duty (14h limit)</th>
                  <th className="text-center text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Violations</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Compliance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {drivers.map((driver) => (
                  <tr key={driver.driver_id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{driver.driver_name}</p>
                        <p className="text-xs text-muted-foreground">{driver.vehicle_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${dutyColors[driver.current_duty_status] ?? dutyColors['off_duty'] ?? 'bg-muted-foreground/20 text-muted-foreground border-border'}`}>
                        {dutyLabels[driver.current_duty_status] ?? driver.current_duty_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <HoursBar value={driver.total_driving_hours} max={11} />
                    </td>
                    <td className="px-4 py-3">
                      <HoursBar value={driver.total_on_duty_hours} max={14} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {driver.violation_count > 0 ? (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 text-red-400 text-xs font-bold border border-red-500/30">
                          {driver.violation_count}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${statusColors[driver.status] ?? statusColors['compliant'] ?? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'}`}>
                        {driver.status.charAt(0).toUpperCase() + driver.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Violation Alerts */}
      {violations.length > 0 && (
        <div className="bg-card/50 backdrop-blur border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <h2 className="text-sm font-semibold text-foreground">Active Violations</h2>
            <span className="ml-auto inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
              {violations.length}
            </span>
          </div>
          <div className="divide-y divide-border/50">
            {violations.map((v) => (
              <div key={`${v.log_id}-${v.rule}`} className="px-4 py-3 flex items-start gap-3">
                <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${v.severity === 'violation' ? 'text-red-400' : 'text-amber-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">{v.driver_name}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${severityColors[v.severity] ?? 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}>
                      {ruleLabels[v.rule] ?? v.rule}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{v.description}</p>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {new Date(v.detected_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
